<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useHelpCenter } from '@/composables/useHelpCenter'
import { Button } from '@/components/ui/button'
import FloatingModuleCard from '@/components/FloatingModuleCard.vue'
import { BookOpen, ChevronRight, Sparkles } from 'lucide-vue-next'
import type { HelpModule, HelpModuleConfig } from '@/types/help'

const emit = defineEmits<{
  complete: []
  navigate: [module: HelpModule]
}>()

const { availableModules, markIntroSeen, navigateToModule } = useHelpCenter()

// Estado de la animación
const exploded = ref(false)
const containerRef = ref<HTMLElement | null>(null)

// Física de partículas
interface FloatingCard {
  module: HelpModuleConfig
  x: number
  y: number
  vx: number
  vy: number
  rotation: number
  scale: number
  delay: number
}

const floatingCards = ref<FloatingCard[]>([])
let animationFrame: number | null = null

// Inicializar posiciones de las cards
function initializeCards() {
  if (!containerRef.value) return

  const bounds = containerRef.value.getBoundingClientRect()
  const centerX = bounds.width / 2
  const centerY = bounds.height / 2

  // Tomar hasta 12 módulos para no saturar
  const modules = availableModules.value.slice(0, 12)

  floatingCards.value = modules.map((mod, index) => {
    // Distribuir en círculo alrededor del centro
    const angle = (index / modules.length) * Math.PI * 2
    const radius = Math.min(bounds.width, bounds.height) * 0.35

    // Posición inicial (explotan desde el centro)
    const targetX = centerX + Math.cos(angle) * radius - 80 // 80 = mitad del ancho de la card
    const targetY = centerY + Math.sin(angle) * radius - 25 // 25 = mitad del alto de la card

    // Velocidad inicial aleatoria para el drift
    const speed = 0.3 + Math.random() * 0.2

    return {
      module: mod,
      x: targetX,
      y: targetY,
      vx: (Math.random() - 0.5) * speed,
      vy: (Math.random() - 0.5) * speed,
      rotation: (Math.random() - 0.5) * 6,
      scale: 0.95 + Math.random() * 0.1,
      delay: index * 50,
    }
  })
}

// Actualizar física de las cards
function updatePhysics() {
  if (!containerRef.value || !exploded.value) return

  const bounds = containerRef.value.getBoundingClientRect()
  const padding = 100

  floatingCards.value.forEach((card) => {
    // Actualizar posición
    card.x += card.vx
    card.y += card.vy

    // Rebote suave en los bordes
    if (card.x < padding || card.x > bounds.width - padding - 160) {
      card.vx *= -0.7
      card.x = Math.max(padding, Math.min(bounds.width - padding - 160, card.x))
    }
    if (card.y < padding || card.y > bounds.height - padding - 50) {
      card.vy *= -0.7
      card.y = Math.max(padding, Math.min(bounds.height - padding - 50, card.y))
    }

    // Fricción
    card.vx *= 0.995
    card.vy *= 0.995

    // Drift aleatorio suave
    card.vx += (Math.random() - 0.5) * 0.02
    card.vy += (Math.random() - 0.5) * 0.02

    // Rotación suave
    card.rotation += (Math.random() - 0.5) * 0.1
    card.rotation = Math.max(-8, Math.min(8, card.rotation))
  })

  animationFrame = requestAnimationFrame(updatePhysics)
}

// Explotar las cards
function explode() {
  exploded.value = true
  initializeCards()

  // Iniciar física después de la animación de entrada
  setTimeout(() => {
    animationFrame = requestAnimationFrame(updatePhysics)
  }, 600)
}

// Manejar click en una card
function handleCardClick(moduleId: HelpModule) {
  stopAnimation()
  markIntroSeen()
  navigateToModule(moduleId)
  emit('navigate', moduleId)
  emit('complete')
}

// Omitir intro
function skipIntro() {
  stopAnimation()
  markIntroSeen()
  emit('complete')
}

function stopAnimation() {
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
    animationFrame = null
  }
}

onMounted(() => {
  // Dar tiempo al DOM para renderizar
  setTimeout(initializeCards, 100)
})

onUnmounted(() => {
  stopAnimation()
})
</script>

<template>
  <div
    ref="containerRef"
    class="fixed inset-0 z-[100] bg-background flex items-center justify-center overflow-hidden"
  >
    <!-- Fondo con patrón sutil -->
    <div class="absolute inset-0 bg-grid-pattern opacity-5" />

    <!-- Estado inicial: icono central pulsante -->
    <Transition
      enter-active-class="transition-all duration-500 ease-out"
      leave-active-class="transition-all duration-300 ease-in"
      enter-from-class="opacity-0 scale-50"
      enter-to-class="opacity-100 scale-100"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-150"
    >
      <div v-if="!exploded" class="text-center relative z-10">
        <!-- Círculos decorativos -->
        <div class="absolute inset-0 -m-20">
          <div class="absolute inset-0 rounded-full border border-primary/10 animate-ping-slow" />
          <div
            class="absolute inset-4 rounded-full border border-primary/20 animate-ping-slow"
            style="animation-delay: 0.5s"
          />
          <div
            class="absolute inset-8 rounded-full border border-primary/30 animate-ping-slow"
            style="animation-delay: 1s"
          />
        </div>

        <!-- Botón principal -->
        <button
          class="relative h-32 w-32 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5
                 flex items-center justify-center cursor-pointer
                 shadow-2xl shadow-primary/20 border border-primary/20
                 hover:scale-105 hover:shadow-primary/30 transition-all duration-300
                 group"
          @click="explode"
        >
          <!-- Glow effect -->
          <div
            class="absolute inset-0 rounded-3xl bg-primary/20 blur-xl opacity-50
                      group-hover:opacity-75 transition-opacity"
          />

          <!-- Icono -->
          <div class="relative">
            <BookOpen class="h-14 w-14 text-primary" />
            <Sparkles
              class="absolute -top-2 -right-2 h-5 w-5 text-yellow-500 animate-pulse"
            />
          </div>
        </button>

        <!-- Texto -->
        <div class="mt-8 space-y-2">
          <h2 class="text-2xl font-bold">Centro de Ayuda</h2>
          <p class="text-muted-foreground">Toca para explorar los módulos</p>
        </div>
      </div>
    </Transition>

    <!-- Estado explosión: cards flotantes -->
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
    >
      <div v-if="exploded" class="absolute inset-0">
        <!-- Cards flotantes -->
        <TransitionGroup
          enter-active-class="transition-all duration-500 ease-out"
          enter-from-class="opacity-0 scale-0"
          enter-to-class="opacity-100 scale-100"
        >
          <FloatingModuleCard
            v-for="card in floatingCards"
            :key="card.module.id"
            :module="card.module"
            :x="card.x"
            :y="card.y"
            :rotation="card.rotation"
            :scale="card.scale"
            :delay="card.delay"
            @click="handleCardClick(card.module.id)"
          />
        </TransitionGroup>

        <!-- Texto central -->
        <div
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                    text-center pointer-events-none"
        >
          <p class="text-lg text-muted-foreground/60">
            Selecciona un módulo para ver sus guías
          </p>
        </div>

        <!-- Botón skip -->
        <div class="absolute bottom-8 left-1/2 -translate-x-1/2">
          <Button variant="ghost" size="lg" class="gap-2" @click="skipIntro">
            Ir al Centro de Ayuda
            <ChevronRight class="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.bg-grid-pattern {
  background-image: radial-gradient(circle, currentColor 1px, transparent 1px);
  background-size: 24px 24px;
}

@keyframes ping-slow {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.25;
  }
  100% {
    transform: scale(1);
    opacity: 0.5;
  }
}

.animate-ping-slow {
  animation: ping-slow 3s ease-in-out infinite;
}
</style>
